import type { NotebookCtx } from '../game/scenes/notebook/tabs';
import { HERO_LOOKS, LOOK_COINS, LOOK_THB, lookItem, type HeroLook } from '@shared/looks';
import { shopProduct } from '@shared/shop';
import { buyCosmetic } from '../game/systems/progression';
import { t } from '../i18n';
import { button, thumb } from './components';
import { h } from './dom';
import { buyPremium } from './premiumShop';

/**
 * Ready-made heroes (Notebook → Hero): finished character art to wear instead
 * of a drawing, for coins or 29 baht. Only looks whose art has been delivered
 * are offered; with none yet the section is left out.
 */
export function readyHeroes(ctx: NotebookCtx): HTMLElement | null {
  const { app } = ctx;
  const looks = HERO_LOOKS.filter((l) => app.assets.has(`hero:${l.id}`));
  if (!looks.length) return null;
  const save = app.store.state;
  const hero = app.store.hero;
  const wear = (id: string | null): void =>
    app.store.update(['hero'], (s) => {
      const hh = s.heroes.find((x) => x.id === s.activeHeroId);
      if (hh) hh.look = id;
    });
  const card = (l: HeroLook): HTMLElement => {
    const owned = save.cosmetics.owned.includes(lookItem(l.id));
    const wearing = hero?.look === l.id;
    const product = shopProduct(`hero:${l.id}`);
    return h(
      'li',
      { class: `cosmetic-card ready-hero paper-scrap ${owned ? 'owned' : ''} ${wearing ? 'wearing' : ''}` },
      owned ? null : h('span', { class: 'premium-ribbon', 'aria-hidden': 'true' }, `฿${LOOK_THB}`),
      thumb(app, null, `hero:${l.id}`, 110),
      h('b', { class: 'cosmetic-name' }, t(l.name)),
      h('small', { class: 'cosmetic-note' }, t(l.note)),
      h(
        'div',
        { class: 'premium-actions' },
        !owned
          ? [
              button(h('span', null, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${LOOK_COINS.toLocaleString()}`), {
                small: true,
                color: 'yellow',
                disabled: save.coins < LOOK_COINS,
                title: save.coins < LOOK_COINS ? t('Not enough coins yet') : t('Buy {name}', { name: t(l.name) }),
                sfx: app,
                onClick: () =>
                  app.store.update(['inventory', 'hero'], (s) => {
                    if (!buyCosmetic(s, lookItem(l.id))) return;
                    // Worn straight away by the hero in play.
                    const hh = s.heroes.find((x) => x.id === s.activeHeroId);
                    if (hh) hh.look = l.id;
                    app.toast(t('Bought {name}!', { name: t(l.name) }), 'success');
                  }),
              }),
              product ? button(`฿${LOOK_THB}`, { small: true, color: 'pink', title: t('Buy with real money'), sfx: app, onClick: () => void buyPremium(app, product) }) : null,
            ]
          : !hero
            ? h('span', { class: 'stamp' }, t('OWNED'))
            : wearing
              ? [h('span', { class: 'stamp' }, t('WEARING')), button(t('Use my drawing'), { small: true, variant: 'ghost', sfx: app, onClick: () => wear(null) })]
              : button(t('Wear it'), { small: true, color: 'green', title: t('{hero} becomes {name}', { hero: hero.name, name: t(l.name) }), sfx: app, onClick: () => wear(l.id) }),
      ),
    );
  };
  return h(
    'section',
    { class: 'ready-heroes' },
    h('h3', { class: 'section-title' }, '✨ ', t('Ready-made Heroes')),
    h('p', { class: 'hand-hint' }, t('Finished characters to wear instead of your drawing. Looks only: your class, skill and stats stay the same.')),
    h('ul', { class: 'cosmetic-list' }, looks.map(card)),
  );
}
